import React, { useRef, useState } from 'react';
import Field from '../Field';
import Button from '../Button';
import { LOGO_WORDING as W } from '../../domain/logo';
import { makeLogo as makeLogoInBrowser, LogoError } from '../../lib/logoImage';
import s from './settings.module.css';

// The studio's logo (#498), where MMS keeps it: the first field of Edit
// Studio Profile, and again under Accounts & Invoices → Invoice Formatting.
//
// ── What MMS does ─────────────────────────────────────────────────────────
// A square thumbnail with a pencil button, and under it "This logo will
// appear on all business reports such as invoices." What its upload dialog
// accepts is unrecorded (#524). We show the thumbnail and name the two actions
// in words — Choose Image / Change Image, and Remove — and accept any picture a
// browser can open (core/src/logo.rs says which are refused, and the size
// bound); it is fitted and re-encoded before it is kept.
//
// ── When it saves ─────────────────────────────────────────────────────────
// Like everything in Business Settings, as soon as it is a complete decision:
// a picture once it has been made into a logo, Remove at once. The write is
// `onChange({ logo })`, one field of the studio document, through the same
// queue as every other setting — so a logo chosen offline is saved offline.
//
// `makeLogo` is a prop so the ui tests can hand in the plan's walk with a
// fake canvas; jsdom has none.
export default function LogoField({ logo, onChange, disabled = false, makeLogo = makeLogoInBrowser }) {
  const input = useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const choose = () => {
    setError(null);
    input.current?.click();
  };

  const picked = (event) => {
    const file = event.target.files?.[0] || null;
    // Cleared so picking the same file again after a refusal still fires.
    event.target.value = '';
    if (!file) return;
    setBusy(true);
    setError(null);
    makeLogo(file).then(
      (uri) => {
        setBusy(false);
        onChange({ logo: uri });
      },
      (err) => {
        setBusy(false);
        setError(err instanceof LogoError ? err.message : W.unreadable);
      },
    );
  };

  const remove = () => {
    setError(null);
    onChange({ logo: null });
  };

  return (
    // No `htmlFor`: a <label> pointing at the button would become the
    // button's name, and "Logo" says less than "Choose Image" does.
    <Field label={W.label} hint={W.hint} error={error}>
      <div className={s.logoRow}>
        <div className={s.logoBox} data-testid="logo-thumbnail">
          {logo
            ? <img className={s.logoImage} src={logo} alt={W.alt} />
            : <span className={s.logoNone}>{W.none}</span>}
        </div>
        <div className={s.logoActions}>
          <Button variant="secondary" size="sm" onClick={choose} loading={busy} disabled={disabled}>
            {logo ? W.change : W.choose}
          </Button>
          {logo ? (
            <Button variant="ghost" size="sm" onClick={remove} disabled={disabled || busy}>
              {W.remove}
            </Button>
          ) : null}
          {busy ? <span className={s.note} role="status">{W.working}</span> : null}
        </div>
        <input
          ref={input}
          hidden
          type="file"
          accept="image/*"
          tabIndex={-1}
          aria-hidden="true"
          data-testid="logo-file"
          onChange={picked}
        />
      </div>
    </Field>
  );
}
